import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import IconAdd from '@mui/icons-material/Add';
import IconEdit from '@mui/icons-material/Edit';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import DateChip from '../moduleCard/DateChip';
import MealPlanPopup, { POPUP_MODE_CREATE, POPUP_MODE_EDIT, POPUP_MODE_VERSION } from '../../../../popup/MealPlanPopup/MealPlanPopup';
import MealCard from './MealCard';
import MealPlanExample from './MealPlanExample';
import MealPlanVersionList from './MealPlanVersionList';
import MealPlanExport, { exportMealPlanPdf } from './MealPlanExport';
import MealPlanWhatsAppButton from './MealPlanWhatsAppButton';
import { formatDate, getCurrentPlan, normalizeMeals, sortPlansByDateDesc } from './mealPlanModel';

/**
 * Módulo "Plano alimentar": plano vigente do paciente como ele o recebe
 * (refeições com porções e substituições, mais orientações gerais), versões
 * anteriores no histórico, exportação em PDF e envio do PDF ao paciente por
 * WhatsApp (quando a clínica tem o número próprio configurado).
 *
 * `data`: planos do paciente (todos, de qualquer atendimento).
 * `bodyMeasurements`: avaliações de medidas, só para a dica de peso/altura no popup.
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const MealPlan = ({
  data,
  patientId,
  appointmentId,
  patientName,
  professionalId,
  companyConfig,
  bodyMeasurements,
  enabled,
  collapsible = false,
  onUpdated,
}) => {
  const [plans, setPlans] = useState([]);
  const [popup, setPopup] = useState(null);
  const exportRef = useRef(null);

  useEffect(() => {
    const list = Array.isArray(data) ? data : [];
    setPlans(list.map((plan) => ({ ...plan, meals: normalizeMeals(plan.meals) })));
  }, [data]);

  const current = useMemo(() => getCurrentPlan(plans), [plans]);
  const versions = useMemo(
    () => sortPlansByDateDesc(plans).filter((plan) => current && String(plan.id) !== String(current.id)),
    [plans, current],
  );
  const ownPlan = appointmentId
    ? plans.find((plan) => String(plan.appointmentId) === String(appointmentId)) || null
    : null;

  function popupHideHandler(needsReload) {
    setPopup(null);
    if (needsReload && onUpdated) onUpdated();
  }

  function exportPdf() {
    if (exportRef.current) exportMealPlanPdf(exportRef.current, patientName);
  }

  const exportButton = current && (
    <CustomButton
      variant="outlined"
      style="secondary"
      label="PDF"
      icon={<PictureAsPdfOutlinedIcon />}
      onClick={exportPdf}
    />
  );

  const actions = (
    <>
      {exportButton}
      {current && (
        <MealPlanWhatsAppButton
          patientId={patientId}
          patientName={patientName}
          getElement={() => exportRef.current}
        />
      )}
      {enabled && !current && (
        <CustomButton
          variant="contained"
          style="primary"
          label="Novo plano"
          icon={<IconAdd />}
          onClick={() => setPopup({ mode: POPUP_MODE_CREATE, plan: null })}
        />
      )}
      {enabled && current && (
        <CustomButton
          variant={ownPlan ? 'contained' : 'outlined'}
          style="primary"
          label="Editar plano"
          icon={<IconEdit />}
          onClick={() => setPopup({ mode: POPUP_MODE_EDIT, plan: current })}
        />
      )}
      {enabled && current && !ownPlan && (
        <CustomButton
          variant="contained"
          style="primary"
          label="Nova versão"
          icon={<ContentCopyOutlinedIcon />}
          onClick={() => setPopup({ mode: POPUP_MODE_VERSION, plan: current })}
        />
      )}
    </>
  );

  return (
    <>
      <ModuleCard
        accent="amber"
        title="Plano alimentar"
        icon={<RestaurantMenuOutlinedIcon />}
        description="Refeições, porções e substituições para o paciente"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {!current ? (
            enabled
              ? <MealPlanExample />
              : <ModuleEmptyState icon={<RestaurantMenuOutlinedIcon />} text="Nenhum plano alimentar montado." />
          ) : (
            <>
              <div className={styles.context}>
                <DateChip label={formatDate(current.createdAt)} />
                {current.title && <span className={styles.planTitle}>{current.title}</span>}
                <span className={parts.muted}>
                  {current.startsAt && `Início em ${formatDate(current.startsAt)} · `}
                  {versions.length === 0 ? 'Plano vigente' : `Plano vigente, ${versions.length} ${versions.length === 1 ? 'versão anterior' : 'versões anteriores'}`}
                  {ownPlan && String(ownPlan.id) === String(current.id) && ' · deste atendimento'}
                </span>
              </div>

              <div className={styles.meals}>
                {current.meals.map((meal) => <MealCard key={meal.key} meal={meal} />)}
              </div>

              {current.guidelines && (
                <div className={styles.guidelines}>
                  <span className={parts.sectionLabel}>Orientações gerais</span>
                  <span className={parts.itemText}>{current.guidelines}</span>
                </div>
              )}

              <MealPlanVersionList
                versions={versions}
                appointmentId={appointmentId}
                onEdit={enabled ? (plan) => setPopup({ mode: POPUP_MODE_EDIT, plan }) : null}
              />
            </>
          )}
        </div>
      </ModuleCard>

      <MealPlanExport
        ref={exportRef}
        plan={current}
        patient={{ name: patientName }}
        companyConfig={companyConfig}
        professionalId={professionalId}
      />

      {popup && (
        <MealPlanPopup
          mode={popup.mode}
          plan={popup.plan}
          patientId={patientId}
          appointmentId={appointmentId}
          bodyMeasurements={bodyMeasurements}
          OnHide={popupHideHandler}
        />
      )}
    </>
  );
};

export default MealPlan;
